import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import destinationsData from '../data/destinationsData';

const CARD_WIDTH = 288;
const CARD_HEIGHT = 400;
const VISIBLE_CARDS = 3;

const additionalLocations = [
  'guadalajara',
  'oaxaca',
  // Add any other slugs for additional locations here
];

export default function DestinationCarousel({ destinations = destinationsData, language = 'en' }) {
  const scrollRef = useRef(null);
  const [scrollDirection, setScrollDirection] = useState(null); // 'left' | 'right' | null
  const frameId = useRef(null);

  // Infinite scroll: clone first and last N cards
  const clonesBefore = destinations.slice(-VISIBLE_CARDS);
  const clonesAfter = destinations.slice(0, VISIBLE_CARDS);
  const allSlides = [...clonesBefore, ...destinations, ...clonesAfter];
  const realStartIdx = clonesBefore.length;
  const realEndIdx = realStartIdx + destinations.length - 1;

  // On mount, set initial scroll position to first real card
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = realStartIdx * CARD_WIDTH;
    }
  }, [realStartIdx]);

  // Infinite scroll boundary check
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft } = scrollRef.current;
    const leftBoundary = (realStartIdx - 1) * CARD_WIDTH;
    const rightBoundary = (realEndIdx + 1) * CARD_WIDTH;
    if (scrollLeft < leftBoundary) {
      scrollRef.current.scrollLeft = realEndIdx * CARD_WIDTH;
    } else if (scrollLeft > rightBoundary) {
      scrollRef.current.scrollLeft = realStartIdx * CARD_WIDTH;
    }
  };

  // Auto-scroll on hover
  useEffect(() => {
    if (!scrollDirection) return;
    const scrollStep = 14; // pixels per frame (increased for even faster auto-scroll)
    const scroll = () => {
      if (!scrollRef.current) return;
      if (scrollDirection === 'left') {
        scrollRef.current.scrollLeft -= scrollStep;
      } else if (scrollDirection === 'right') {
        scrollRef.current.scrollLeft += scrollStep;
      }
      frameId.current = requestAnimationFrame(scroll);
    };
    frameId.current = requestAnimationFrame(scroll);
    return () => {
      if (frameId.current) cancelAnimationFrame(frameId.current);
    };
  }, [scrollDirection]);

  const scrollByCard = (direction) => {
    if (!scrollRef.current) return;
    const { scrollLeft } = scrollRef.current;
    const newScroll = direction === 'left'
      ? scrollLeft - CARD_WIDTH
      : scrollLeft + CARD_WIDTH;
    scrollRef.current.scrollTo({ left: newScroll, behavior: 'smooth' });
  };

  return (
    <div className="relative w-full py-8">
      {/* Overlayed Arrows */}
      <button
        aria-label="Scroll left"
        className="hidden md:block absolute z-20 top-1/2 left-2 md:left-[calc(50%-472px)] -translate-y-1/2 bg-white/80 hover:bg-[#bfa76a] hover:text-white text-[#bfa76a] rounded-full p-2 shadow-lg transition-opacity duration-200"
        style={{ width: 48, height: 48 }}
        onClick={() => scrollByCard('left')}
        onMouseEnter={() => setScrollDirection('left')}
        onMouseLeave={() => setScrollDirection(null)}
      >
        <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        aria-label="Scroll right"
        className="hidden md:block absolute z-20 top-1/2 right-2 md:right-[calc(50%-472px)] -translate-y-1/2 bg-white/80 hover:bg-[#bfa76a] hover:text-white text-[#bfa76a] rounded-full p-2 shadow-lg transition-opacity duration-200"
        style={{ width: 48, height: 48 }}
        onClick={() => scrollByCard('right')}
        onMouseEnter={() => setScrollDirection('right')}
        onMouseLeave={() => setScrollDirection(null)}
      >
        <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className="overflow-x-scroll no-scrollbar max-w-4xl mx-auto px-6"
        onScroll={handleScroll}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex gap-4" style={{ minWidth: 900 }}>
          {allSlides.map((dest, i) => {
            // Check if a guide exists for this destination (slug in destinationsData)
            const isAdditional = additionalLocations.includes(dest.slug);
            const hasGuide = !!dest.slug && !isAdditional;
            const linkTo = hasGuide ? `/destinations/${dest.slug}` : '/destinations';
            return (
              <Link
                to={linkTo}
                key={i + dest.slug}
                className="flex-shrink-0 w-72 h-[400px] rounded-3xl overflow-hidden relative shadow-lg bg-white"
                style={{ minWidth: CARD_WIDTH, height: CARD_HEIGHT }}
              >
                <img
                  src={dest.image}
                  alt={language === 'es' ? dest.title_es : dest.title}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  style={{ zIndex: 1 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#181c24]/80 to-transparent" style={{ zIndex: 2 }} />
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  <h3 className="text-2xl font-playfair mb-1 drop-shadow-[0_2px_12px_rgba(24,28,36,0.9)] text-[#e3c27b] font-semibold">
                    {language === 'es' ? dest.title_es : dest.title}
                  </h3>
                  <p className="text-base font-light drop-shadow-[0_1px_8px_rgba(24,28,36,0.8)] text-white">
                    {language === 'es' ? dest.desc_es : dest.desc}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
        <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
      </div>
    </div>
  );
}